import { useCallback, useEffect, useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { CircularProgress } from '@mui/joy'
import Modal from '@mui/joy/Modal'
import ModalDialog from '@mui/joy/ModalDialog'
import { Add, Block, CheckCircle, Close, DeleteOutline, Person, Refresh, Schedule, Search as SearchIcon, Shield, Verified } from '@mui/icons-material'
import { PROXY_ALLOWLIST_DOMAIN_REGEX } from '@numeo/types'
import { proxyAllowlistApi, ProxyAllowlistRequestDto, ProxyAllowlistRequestStatus, ProxyAllowlistUrlDto } from './api'

const ease: [number, number, number, number] = [0.23, 1, 0.32, 1]

// ─── Visual helpers ─────────────────────────────────────────────────────────

const STATUS_STYLE: Record<ProxyAllowlistRequestStatus, { label: string; chip: string; dot: string }> = {
    pending: {
        label: 'Pending',
        chip: 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200',
        dot: 'bg-amber-500',
    },
    approved: {
        label: 'Approved',
        chip: 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200',
        dot: 'bg-emerald-500',
    },
    rejected: {
        label: 'Rejected',
        chip: 'bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200',
        dot: 'bg-rose-500',
    },
}

const AVATAR_COLORS = [
    'bg-blue-100 text-blue-700',
    'bg-violet-100 text-violet-700',
    'bg-emerald-100 text-emerald-700',
    'bg-amber-100 text-amber-700',
    'bg-rose-100 text-rose-700',
    'bg-cyan-100 text-cyan-700',
    'bg-indigo-100 text-indigo-700',
]

const getInitials = (value: string): string => {
    if (!value) return '?'
    const cleaned = value.split('@')[0]
    const parts = cleaned.split(/[._\s-]+/).filter(Boolean)
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
    return cleaned.substring(0, 2).toUpperCase()
}

const getAvatarColor = (seed: string): string => {
    let hash = 0
    for (let i = 0; i < seed.length; i++) hash = seed.charCodeAt(i) + ((hash << 5) - hash)
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

const fmtDate = (d: Date | string | undefined): string => {
    if (!d) return '—'
    const parsed = typeof d === 'string' ? new Date(d) : d
    return parsed.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

const fmtRelative = (d: Date | string | undefined): string => {
    if (!d) return ''
    const parsed = typeof d === 'string' ? new Date(d) : d
    const diff = Date.now() - parsed.getTime()
    const m = Math.floor(diff / 60000)
    if (m < 1) return 'just now'
    if (m < 60) return `${m}m ago`
    const h = Math.floor(m / 60)
    if (h < 24) return `${h}h ago`
    const dys = Math.floor(h / 24)
    if (dys < 30) return `${dys}d ago`
    return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// ─── Page ───────────────────────────────────────────────────────────────────

type View = 'requests' | 'urls' | 'blocklist'

export default function ProxyAllowlistPage() {
    const [view, setView] = useState<View>('requests')

    // Requests state
    const [requests, setRequests] = useState<ProxyAllowlistRequestDto[]>([])
    const [requestsLoading, setRequestsLoading] = useState(true)
    const [requestsError, setRequestsError] = useState('')
    const [statusFilter, setStatusFilter] = useState<ProxyAllowlistRequestStatus | 'all'>(ProxyAllowlistRequestStatus.PENDING)
    const [requestSearch, setRequestSearch] = useState('')
    const [pendingAction, setPendingAction] = useState<string | null>(null)
    const [rejectDialog, setRejectDialog] = useState<{ id: string; domain: string } | null>(null)
    const [rejectReason, setRejectReason] = useState('')

    // URLs state
    const [urls, setUrls] = useState<ProxyAllowlistUrlDto[]>([])
    const [urlsLoading, setUrlsLoading] = useState(true)
    const [urlsError, setUrlsError] = useState('')
    const [urlSearch, setUrlSearch] = useState('')
    const [newDomain, setNewDomain] = useState('')
    const [newHidden, setNewHidden] = useState(false)
    const [addingUrl, setAddingUrl] = useState(false)
    const [addUrlError, setAddUrlError] = useState('')
    const [addUrlSuccess, setAddUrlSuccess] = useState('')
    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)

    // Blocklist state (forced-DIRECT domains; never surfaced to end users)
    const [blocklist, setBlocklist] = useState<ProxyAllowlistUrlDto[]>([])
    const [blocklistLoading, setBlocklistLoading] = useState(true)
    const [blocklistError, setBlocklistError] = useState('')
    const [blockSearch, setBlockSearch] = useState('')
    const [newBlockDomain, setNewBlockDomain] = useState('')
    const [addingBlock, setAddingBlock] = useState(false)
    const [addBlockError, setAddBlockError] = useState('')
    const [addBlockSuccess, setAddBlockSuccess] = useState('')
    const [confirmDeleteBlockId, setConfirmDeleteBlockId] = useState<string | null>(null)

    // Counts shown in stat cards + tab badges — derived from a separate "all" pull
    // so counts don't change when the user filters the requests view.
    const [counts, setCounts] = useState<{ pending: number; approved: number; rejected: number }>({ pending: 0, approved: 0, rejected: 0 })

    // ─── Loaders ───
    const loadRequests = useCallback(async () => {
        setRequestsLoading(true)
        setRequestsError('')
        try {
            const next = await proxyAllowlistApi.listRequests(statusFilter === 'all' ? undefined : statusFilter)
            setRequests(next)
        } catch {
            setRequestsError('Failed to load requests')
        } finally {
            setRequestsLoading(false)
        }
    }, [statusFilter])

    const loadCounts = useCallback(async () => {
        try {
            const [p, a, r] = await Promise.all([
                proxyAllowlistApi.listRequests(ProxyAllowlistRequestStatus.PENDING),
                proxyAllowlistApi.listRequests(ProxyAllowlistRequestStatus.APPROVED),
                proxyAllowlistApi.listRequests(ProxyAllowlistRequestStatus.REJECTED),
            ])
            setCounts({ pending: p.length, approved: a.length, rejected: r.length })
        } catch {
            // counts are non-blocking — leave them at last known value
        }
    }, [])

    const loadUrls = useCallback(async () => {
        setUrlsLoading(true)
        setUrlsError('')
        try {
            const next = await proxyAllowlistApi.listUrls()
            setUrls(next)
        } catch {
            setUrlsError('Failed to load URLs')
        } finally {
            setUrlsLoading(false)
        }
    }, [])

    useEffect(() => {
        loadRequests()
    }, [loadRequests])

    const loadBlocklist = useCallback(async () => {
        setBlocklistLoading(true)
        setBlocklistError('')
        try {
            const next = await proxyAllowlistApi.listBlocklist()
            setBlocklist(next)
        } catch {
            setBlocklistError('Failed to load blocklist')
        } finally {
            setBlocklistLoading(false)
        }
    }, [])

    useEffect(() => {
        loadUrls()
        loadCounts()
        loadBlocklist()
    }, [loadUrls, loadCounts, loadBlocklist])

    // ─── Actions ───
    const handleApprove = async (req: ProxyAllowlistRequestDto) => {
        setPendingAction(req.id)
        try {
            await proxyAllowlistApi.approveRequest(req.id)
            await Promise.all([loadRequests(), loadUrls(), loadCounts()])
        } catch (err) {
            setRequestsError(err instanceof Error ? err.message : 'Approval failed')
        } finally {
            setPendingAction(null)
        }
    }

    const openReject = (req: ProxyAllowlistRequestDto) => {
        setRejectDialog({ id: req.id, domain: req.domain })
        setRejectReason('')
    }

    const handleConfirmReject = async () => {
        if (!rejectDialog) return
        setPendingAction(rejectDialog.id)
        try {
            await proxyAllowlistApi.rejectRequest(rejectDialog.id, rejectReason.trim() || undefined)
            setRejectDialog(null)
            await Promise.all([loadRequests(), loadCounts()])
        } catch (err) {
            setRequestsError(err instanceof Error ? err.message : 'Reject failed')
        } finally {
            setPendingAction(null)
        }
    }

    const handleAddUrl = async () => {
        const trimmed = newDomain.trim().toLowerCase()
        setAddUrlError('')
        setAddUrlSuccess('')
        if (!trimmed) {
            setAddUrlError('Enter a domain.')
            return
        }
        if (!PROXY_ALLOWLIST_DOMAIN_REGEX.test(trimmed)) {
            setAddUrlError('Use a bare host (dat.com) or a wildcard (*.dat.com).')
            return
        }
        setAddingUrl(true)
        try {
            await proxyAllowlistApi.addUrl(trimmed, newHidden)
            setNewDomain('')
            setNewHidden(false)
            setAddUrlSuccess(`Added ${trimmed}${newHidden ? ' (hidden)' : ''}`)
            await loadUrls()
            window.setTimeout(() => setAddUrlSuccess(''), 3000)
        } catch (err) {
            setAddUrlError(err instanceof Error ? err.message : 'Add failed')
        } finally {
            setAddingUrl(false)
        }
    }

    const handleDeleteConfirmed = async (id: string) => {
        setPendingAction(id)
        try {
            await proxyAllowlistApi.deleteUrl(id)
            setConfirmDeleteId(null)
            await loadUrls()
        } catch (err) {
            setUrlsError(err instanceof Error ? err.message : 'Delete failed')
        } finally {
            setPendingAction(null)
        }
    }

    const handleAddBlock = async () => {
        const trimmed = newBlockDomain.trim().toLowerCase()
        setAddBlockError('')
        setAddBlockSuccess('')
        if (!trimmed) {
            setAddBlockError('Enter a domain.')
            return
        }
        if (!PROXY_ALLOWLIST_DOMAIN_REGEX.test(trimmed)) {
            setAddBlockError('Use a bare host (ads.dat.com) or a wildcard (*.doubleclick.net).')
            return
        }
        setAddingBlock(true)
        try {
            await proxyAllowlistApi.addBlocklist(trimmed)
            setNewBlockDomain('')
            setAddBlockSuccess(`Blocked ${trimmed}`)
            await loadBlocklist()
            window.setTimeout(() => setAddBlockSuccess(''), 3000)
        } catch (err) {
            setAddBlockError(err instanceof Error ? err.message : 'Add failed')
        } finally {
            setAddingBlock(false)
        }
    }

    const handleDeleteBlockConfirmed = async (id: string) => {
        setPendingAction(id)
        try {
            await proxyAllowlistApi.deleteBlocklist(id)
            setConfirmDeleteBlockId(null)
            await loadBlocklist()
        } catch (err) {
            setBlocklistError(err instanceof Error ? err.message : 'Delete failed')
        } finally {
            setPendingAction(null)
        }
    }

    const handleToggleHidden = async (id: string, hidden: boolean) => {
        setPendingAction(id)
        try {
            await proxyAllowlistApi.setUrlHidden(id, hidden)
            await loadUrls()
        } catch (err) {
            setUrlsError(err instanceof Error ? err.message : 'Update failed')
        } finally {
            setPendingAction(null)
        }
    }

    // ─── Derived ───
    const filteredRequests = useMemo(() => {
        if (!requestSearch.trim()) return requests
        const q = requestSearch.trim().toLowerCase()
        return requests.filter(
            (r) =>
                r.domain.toLowerCase().includes(q) ||
                (r.requestedBy.email ?? '').toLowerCase().includes(q) ||
                (r.requestedBy.projectName ?? '').toLowerCase().includes(q) ||
                (r.reason ?? '').toLowerCase().includes(q)
        )
    }, [requests, requestSearch])

    const filteredUrls = useMemo(() => {
        if (!urlSearch.trim()) return urls
        const q = urlSearch.trim().toLowerCase()
        return urls.filter((u) => u.domain.toLowerCase().includes(q) || (u.addedByEmail ?? '').toLowerCase().includes(q))
    }, [urls, urlSearch])

    const filteredBlocklist = useMemo(() => {
        if (!blockSearch.trim()) return blocklist
        const q = blockSearch.trim().toLowerCase()
        return blocklist.filter((u) => u.domain.toLowerCase().includes(q) || (u.addedByEmail ?? '').toLowerCase().includes(q))
    }, [blocklist, blockSearch])

    const STAT_CARDS = [
        { label: 'Pending', value: counts.pending, color: 'border-t-amber-500', iconBg: 'bg-amber-50', iconColor: 'text-amber-600', Icon: Schedule },
        {
            label: 'Approved',
            value: counts.approved,
            color: 'border-t-emerald-500',
            iconBg: 'bg-emerald-50',
            iconColor: 'text-emerald-600',
            Icon: CheckCircle,
        },
        { label: 'Rejected', value: counts.rejected, color: 'border-t-rose-500', iconBg: 'bg-rose-50', iconColor: 'text-rose-600', Icon: Block },
        { label: 'Allowed Domains', value: urls.length, color: 'border-t-indigo-500', iconBg: 'bg-indigo-50', iconColor: 'text-indigo-600', Icon: Verified },
    ]

    return (
        <div className="w-full p-6">
            {/* Breadcrumb */}
            <div className="mb-5 flex items-center gap-1.5 text-xs text-gray-400">
                <span>Home</span>
                <span>/</span>
                <span className="font-medium text-gray-600">Proxy Allowlist</span>
            </div>

            {/* Page header */}
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, ease }}
                className="mb-6 flex items-center gap-3"
            >
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50">
                    <Shield sx={{ fontSize: 18 }} className="text-indigo-600" />
                </div>
                <div className="flex-1">
                    <h1 className="text-xl font-bold text-gray-900">Proxy Allowlist</h1>
                    <p className="text-sm text-gray-500">
                        Manage which domains the extension's egress proxy routes through. Operator-only — affects every tenant.
                    </p>
                </div>
                <button
                    onClick={() => {
                        loadRequests()
                        loadUrls()
                        loadCounts()
                        loadBlocklist()
                    }}
                    className="flex h-9 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-50"
                >
                    <Refresh sx={{ fontSize: 16 }} />
                    Refresh
                </button>
            </motion.div>

            {/* Stat cards */}
            <div className="mb-6 grid grid-cols-4 gap-3">
                {STAT_CARDS.map((stat, i) => (
                    <motion.div
                        key={stat.label}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: i * 0.05, ease }}
                    >
                        <div
                            className={`rounded-xl border border-gray-100 border-t-[3px] bg-white p-4 shadow-sm transition-shadow duration-200 hover:shadow-md ${stat.color}`}
                        >
                            <div className="mb-2 flex items-center justify-between">
                                <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{stat.label}</p>
                                <div className={`flex h-7 w-7 items-center justify-center rounded-md ${stat.iconBg}`}>
                                    <stat.Icon sx={{ fontSize: 14 }} className={stat.iconColor} />
                                </div>
                            </div>
                            <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* View segmented control */}
            <div className="mb-4 flex items-center justify-between gap-3">
                <div role="tablist" aria-label="View" className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1">
                    {(
                        [
                            { value: 'requests' as View, label: 'Requests', count: counts.pending },
                            { value: 'urls' as View, label: 'Allowed URLs', count: urls.length },
                            { value: 'blocklist' as View, label: 'Blocklist', count: blocklist.length },
                        ] as const
                    ).map((opt) => {
                        const active = opt.value === view
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                role="tab"
                                aria-selected={active}
                                onClick={() => setView(opt.value)}
                                className={[
                                    'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                                    active ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200' : 'text-gray-600 hover:text-gray-900',
                                ].join(' ')}
                            >
                                <span>{opt.label}</span>
                                {opt.count > 0 && (
                                    <span
                                        className={[
                                            'inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold',
                                            active ? 'bg-indigo-50 text-indigo-700' : 'bg-gray-200 text-gray-600',
                                        ].join(' ')}
                                    >
                                        {opt.count}
                                    </span>
                                )}
                            </button>
                        )
                    })}
                </div>
            </div>

            {/* ─── Requests view ─── */}
            {view === 'requests' && (
                <RequestsCard
                    requests={filteredRequests}
                    loading={requestsLoading}
                    error={requestsError}
                    statusFilter={statusFilter}
                    onStatusFilterChange={setStatusFilter}
                    search={requestSearch}
                    onSearchChange={setRequestSearch}
                    pendingAction={pendingAction}
                    onApprove={handleApprove}
                    onOpenReject={openReject}
                    onClearError={() => setRequestsError('')}
                />
            )}

            {/* ─── URLs view ─── */}
            {view === 'urls' && (
                <UrlsCard
                    urls={filteredUrls}
                    loading={urlsLoading}
                    error={urlsError}
                    search={urlSearch}
                    onSearchChange={setUrlSearch}
                    newDomain={newDomain}
                    onNewDomainChange={(v) => {
                        setNewDomain(v)
                        if (addUrlError) setAddUrlError('')
                        if (addUrlSuccess) setAddUrlSuccess('')
                    }}
                    newHidden={newHidden}
                    onNewHiddenChange={setNewHidden}
                    onAddUrl={handleAddUrl}
                    adding={addingUrl}
                    addError={addUrlError}
                    addSuccess={addUrlSuccess}
                    confirmDeleteId={confirmDeleteId}
                    onAskConfirmDelete={setConfirmDeleteId}
                    onDeleteConfirmed={handleDeleteConfirmed}
                    onToggleHidden={handleToggleHidden}
                    pendingAction={pendingAction}
                    onClearError={() => setUrlsError('')}
                />
            )}

            {/* ─── Blocklist view ─── */}
            {view === 'blocklist' && (
                <BlocklistCard
                    urls={filteredBlocklist}
                    loading={blocklistLoading}
                    error={blocklistError}
                    search={blockSearch}
                    onSearchChange={setBlockSearch}
                    newDomain={newBlockDomain}
                    onNewDomainChange={(v) => {
                        setNewBlockDomain(v)
                        if (addBlockError) setAddBlockError('')
                        if (addBlockSuccess) setAddBlockSuccess('')
                    }}
                    onAddUrl={handleAddBlock}
                    adding={addingBlock}
                    addError={addBlockError}
                    addSuccess={addBlockSuccess}
                    confirmDeleteId={confirmDeleteBlockId}
                    onAskConfirmDelete={setConfirmDeleteBlockId}
                    onDeleteConfirmed={handleDeleteBlockConfirmed}
                    pendingAction={pendingAction}
                    onClearError={() => setBlocklistError('')}
                />
            )}

            {/* Reject reason dialog */}
            <Modal open={!!rejectDialog} onClose={() => !pendingAction && setRejectDialog(null)}>
                <ModalDialog variant="outlined" role="alertdialog" sx={{ maxWidth: 480, p: 0, overflow: 'hidden' }}>
                    <div className="border-b border-gray-100 bg-gradient-to-r from-rose-50 to-rose-50/40 px-5 py-4">
                        <div className="flex items-center gap-2">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-rose-100">
                                <Block sx={{ fontSize: 18 }} className="text-rose-600" />
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-gray-900">Reject this request?</h3>
                                <p className="font-mono text-xs text-gray-500">{rejectDialog?.domain}</p>
                            </div>
                        </div>
                    </div>
                    <div className="px-5 py-4">
                        <p className="text-sm text-gray-600">
                            The domain won't be added to the allowlist. Add an optional note for the audit trail — it's shown to other admins, not the
                            requester.
                        </p>
                        <textarea
                            placeholder="Reason (optional)…"
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value.slice(0, 500))}
                            rows={3}
                            className="mt-3 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-rose-300 focus:outline-none focus:ring-2 focus:ring-rose-500/20"
                        />
                        <p className="mt-1 text-[11px] text-gray-400">{rejectReason.length}/500</p>
                    </div>
                    <div className="flex items-center justify-end gap-2 border-t border-gray-100 bg-gray-50/50 px-5 py-3">
                        <button
                            onClick={() => setRejectDialog(null)}
                            disabled={!!pendingAction}
                            className="flex h-8 items-center rounded-lg px-3 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 disabled:opacity-60"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleConfirmReject}
                            disabled={pendingAction === rejectDialog?.id}
                            className="flex h-8 items-center gap-1.5 rounded-lg bg-rose-600 px-4 text-sm font-medium text-white transition-colors hover:bg-rose-700 disabled:opacity-60"
                        >
                            {pendingAction === rejectDialog?.id ? (
                                <CircularProgress size="sm" sx={{ '--CircularProgress-size': '12px', color: '#fff' }} />
                            ) : null}
                            Reject
                        </button>
                    </div>
                </ModalDialog>
            </Modal>
        </div>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// RequestsCard
// ─────────────────────────────────────────────────────────────────────────────

interface RequestsCardProps {
    requests: ProxyAllowlistRequestDto[]
    loading: boolean
    error: string
    statusFilter: ProxyAllowlistRequestStatus | 'all'
    onStatusFilterChange: (s: ProxyAllowlistRequestStatus | 'all') => void
    search: string
    onSearchChange: (s: string) => void
    pendingAction: string | null
    onApprove: (req: ProxyAllowlistRequestDto) => void
    onOpenReject: (req: ProxyAllowlistRequestDto) => void
    onClearError: () => void
}

const STATUS_FILTER_OPTIONS: { value: ProxyAllowlistRequestStatus | 'all'; label: string }[] = [
    { value: ProxyAllowlistRequestStatus.PENDING, label: 'Pending' },
    { value: ProxyAllowlistRequestStatus.APPROVED, label: 'Approved' },
    { value: ProxyAllowlistRequestStatus.REJECTED, label: 'Rejected' },
    { value: 'all', label: 'All' },
]

function RequestsCard(props: RequestsCardProps) {
    const { requests, loading, error, statusFilter, onStatusFilterChange, search, onSearchChange, pendingAction, onApprove, onOpenReject, onClearError } = props

    return (
        <div className="rounded-xl border border-gray-100 bg-white shadow-sm">
            {/* Section header */}
            <div className="flex items-center gap-2 border-b border-gray-100 px-5 py-3">
                <Schedule sx={{ fontSize: 16 }} className="text-gray-500" />
                <p className="text-sm font-semibold text-gray-900">Requests</p>
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">{requests.length}</span>
            </div>

            {/* Filter / search strip */}
            <div className="flex flex-wrap items-end gap-3 border-b border-gray-100 bg-gray-50/50 px-5 py-3">
                <div className="flex flex-wrap items-center gap-1.5">
                    {STATUS_FILTER_OPTIONS.map((opt) => {
                        const active = opt.value === statusFilter
                        return (
                            <button
                                key={opt.value}
                                onClick={() => onStatusFilterChange(opt.value)}
                                className={[
                                    'h-7 rounded-full px-2.5 text-xs font-medium transition-colors',
                                    active ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 ring-1 ring-gray-200 hover:bg-gray-50',
                                ].join(' ')}
                            >
                                {opt.label}
                            </button>
                        )
                    })}
                </div>
                <div className="ml-auto w-full max-w-xs">
                    <div className="relative">
                        <SearchIcon sx={{ fontSize: 14 }} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            value={search}
                            onChange={(e) => onSearchChange(e.target.value)}
                            placeholder="Search domain, user, project…"
                            className="h-8 w-full rounded-lg border border-gray-200 bg-white pl-8 pr-3 text-sm focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                        />
                        {search && (
                            <button
                                onClick={() => onSearchChange('')}
                                className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                            >
                                <Close sx={{ fontSize: 13 }} />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {error && (
                <div className="flex items-center justify-between gap-2 border-b border-rose-100 bg-rose-50 px-5 py-2.5 text-sm text-rose-700">
                    <span>{error}</span>
                    <button onClick={onClearError} className="rounded p-1 text-rose-600 hover:bg-rose-100">
                        <Close sx={{ fontSize: 14 }} />
                    </button>
                </div>
            )}

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-gray-50/80 text-left">
                            <th className="w-[52px] px-5 py-3 text-xs font-semibold text-gray-600"></th>
                            <th className="px-2 py-3 text-xs font-semibold text-gray-600">Domain</th>
                            <th className="px-2 py-3 text-xs font-semibold text-gray-600">Requested by</th>
                            <th className="px-2 py-3 text-xs font-semibold text-gray-600">Reason</th>
                            <th className="px-2 py-3 text-xs font-semibold text-gray-600">Status</th>
                            <th className="px-2 py-3 text-xs font-semibold text-gray-600">Created</th>
                            <th className="px-2 py-3 pr-5 text-right text-xs font-semibold text-gray-600">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={7} className="py-12 text-center">
                                    <CircularProgress size="sm" />
                                </td>
                            </tr>
                        ) : requests.length === 0 ? (
                            <tr>
                                <td colSpan={7}>
                                    <EmptyState
                                        icon={<Schedule sx={{ fontSize: 26 }} className="text-gray-400" />}
                                        title={search ? 'No requests match your search' : 'No requests in this view'}
                                        hint={
                                            search
                                                ? 'Try a different search term or clear the filter.'
                                                : 'When users request a new domain from the extension, it shows up here.'
                                        }
                                    />
                                </td>
                            </tr>
                        ) : (
                            requests.map((req, idx) => {
                                const style = STATUS_STYLE[req.status]
                                const isPending = req.status === ProxyAllowlistRequestStatus.PENDING
                                const isBusy = pendingAction === req.id
                                const requesterLabel = req.requestedBy.email ?? 'unknown'
                                return (
                                    <motion.tr
                                        key={req.id}
                                        initial={{ opacity: 0, x: -8 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.2, delay: idx * 0.02, ease }}
                                        className="group border-b border-gray-100 transition-colors duration-150 last:border-b-0 hover:bg-indigo-50/40"
                                    >
                                        <td className="py-3 pl-5 pr-0">
                                            <div
                                                className={`flex h-8 w-8 items-center justify-center rounded-full text-[10px] font-bold ${getAvatarColor(requesterLabel)}`}
                                            >
                                                {getInitials(requesterLabel)}
                                            </div>
                                        </td>
                                        <td className="px-2 py-3">
                                            <span className="rounded bg-gray-50 px-2 py-1 font-mono text-xs text-gray-800 ring-1 ring-inset ring-gray-200">
                                                {req.domain}
                                            </span>
                                        </td>
                                        <td className="px-2 py-3">
                                            <p className="text-sm font-medium text-gray-800">{requesterLabel}</p>
                                            <p className="text-[11px] text-gray-400">{req.requestedBy.projectName ?? '—'}</p>
                                        </td>
                                        <td className="max-w-[260px] px-2 py-3 text-sm text-gray-600">
                                            {req.reason ? (
                                                <p className="line-clamp-2 break-words" title={req.reason}>
                                                    {req.reason}
                                                </p>
                                            ) : (
                                                <span className="text-gray-300">—</span>
                                            )}
                                        </td>
                                        <td className="px-2 py-3">
                                            <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${style.chip}`}>
                                                <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`} />
                                                {style.label}
                                            </span>
                                            {req.status === ProxyAllowlistRequestStatus.REJECTED && req.rejectionReason && (
                                                <p className="mt-1 max-w-[200px] truncate text-[11px] text-gray-400" title={req.rejectionReason}>
                                                    "{req.rejectionReason}"
                                                </p>
                                            )}
                                        </td>
                                        <td className="px-2 py-3">
                                            <p className="text-sm text-gray-600">{fmtRelative(req.createdAt)}</p>
                                            <p className="text-[11px] text-gray-400">{fmtDate(req.createdAt)}</p>
                                        </td>
                                        <td className="px-2 py-3 pr-5 text-right">
                                            {isPending ? (
                                                <div className="inline-flex items-center gap-1.5">
                                                    <button
                                                        onClick={() => onApprove(req)}
                                                        disabled={isBusy}
                                                        className="flex h-7 items-center gap-1 rounded-md bg-emerald-600 px-2.5 text-xs font-medium text-white transition-colors hover:bg-emerald-700 disabled:opacity-60"
                                                    >
                                                        {isBusy ? (
                                                            <CircularProgress size="sm" sx={{ '--CircularProgress-size': '10px', color: '#fff' }} />
                                                        ) : (
                                                            <CheckCircle sx={{ fontSize: 13 }} />
                                                        )}
                                                        Approve
                                                    </button>
                                                    <button
                                                        onClick={() => onOpenReject(req)}
                                                        disabled={isBusy}
                                                        className="flex h-7 items-center gap-1 rounded-md border border-rose-200 bg-white px-2.5 text-xs font-medium text-rose-600 transition-colors hover:bg-rose-50 disabled:opacity-60"
                                                    >
                                                        <Block sx={{ fontSize: 13 }} />
                                                        Reject
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="inline-flex items-center gap-1.5 text-[11px] text-gray-400">
                                                    <Person sx={{ fontSize: 12 }} />
                                                    {req.reviewedBy?.email ?? '—'}
                                                </div>
                                            )}
                                        </td>
                                    </motion.tr>
                                )
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// UrlsCard
// ─────────────────────────────────────────────────────────────────────────────

interface UrlsCardProps {
    urls: ProxyAllowlistUrlDto[]
    loading: boolean
    error: string
    search: string
    onSearchChange: (s: string) => void
    newDomain: string
    onNewDomainChange: (v: string) => void
    newHidden: boolean
    onNewHiddenChange: (v: boolean) => void
    onAddUrl: () => void
    adding: boolean
    addError: string
    addSuccess: string
    confirmDeleteId: string | null
    onAskConfirmDelete: (id: string | null) => void
    onDeleteConfirmed: (id: string) => void
    onToggleHidden: (id: string, hidden: boolean) => void
    pendingAction: string | null
    onClearError: () => void
}

function UrlsCard(props: UrlsCardProps) {
    const {
        urls,
        loading,
        error,
        search,
        onSearchChange,
        newDomain,
        onNewDomainChange,
        newHidden,
        onNewHiddenChange,
        onAddUrl,
        adding,
        addError,
        addSuccess,
        confirmDeleteId,
        onAskConfirmDelete,
        onDeleteConfirmed,
        onToggleHidden,
        pendingAction,
        onClearError,
    } = props

    const visibleUrls = urls.filter((u) => !u.hidden)
    const hiddenUrls = urls.filter((u) => u.hidden)

    const renderUrlRow = (url: ProxyAllowlistUrlDto, idx: number) => {
        const isConfirming = confirmDeleteId === url.id
        const isBusy = pendingAction === url.id
        return (
            <motion.tr
                key={url.id}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.2, delay: idx * 0.02, ease }}
                className="group border-b border-gray-100 transition-colors duration-150 last:border-b-0 hover:bg-indigo-50/40"
            >
                <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                        <span className="rounded bg-gray-50 px-2 py-1 font-mono text-sm text-gray-800 ring-1 ring-inset ring-gray-200">{url.domain}</span>
                        {url.hidden && (
                            <span
                                title="Routed through the proxy but not shown to users"
                                className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-inset ring-amber-200"
                            >
                                hidden
                            </span>
                        )}
                    </div>
                </td>
                <td className="px-2 py-3 text-sm text-gray-700">
                    {url.addedBy === 'system' ? (
                        <span className="inline-flex items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600">system</span>
                    ) : (
                        (url.addedByEmail ?? '—')
                    )}
                </td>
                <td className="px-2 py-3">
                    <p className="text-sm text-gray-600">{fmtRelative(url.createdAt)}</p>
                    <p className="text-[11px] text-gray-400">{fmtDate(url.createdAt)}</p>
                </td>
                <td className="px-2 py-3">
                    {url.addedFromRequestId ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200">
                            <CheckCircle sx={{ fontSize: 11 }} />
                            from request
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700 ring-1 ring-inset ring-indigo-200">
                            <Add sx={{ fontSize: 11 }} />
                            added directly
                        </span>
                    )}
                </td>
                <td className="px-2 py-3 pr-5 text-right">
                    {isConfirming ? (
                        <div className="inline-flex items-center gap-1.5">
                            <span className="text-xs text-gray-500">Remove?</span>
                            <button
                                onClick={() => onDeleteConfirmed(url.id)}
                                disabled={isBusy}
                                className="flex h-7 items-center gap-1 rounded-md bg-rose-600 px-2.5 text-xs font-medium text-white transition-colors hover:bg-rose-700 disabled:opacity-60"
                            >
                                {isBusy ? <CircularProgress size="sm" sx={{ '--CircularProgress-size': '10px', color: '#fff' }} /> : null}
                                Yes
                            </button>
                            <button
                                onClick={() => onAskConfirmDelete(null)}
                                disabled={isBusy}
                                className="flex h-7 items-center rounded-md border border-gray-200 bg-white px-2.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-60"
                            >
                                No
                            </button>
                        </div>
                    ) : (
                        <div className="inline-flex items-center gap-1.5">
                            <button
                                onClick={() => onToggleHidden(url.id, !url.hidden)}
                                disabled={isBusy}
                                className="flex h-7 items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 text-xs font-medium text-gray-600 opacity-0 transition-all hover:bg-gray-50 disabled:opacity-60 group-hover:opacity-100"
                            >
                                {isBusy ? <CircularProgress size="sm" sx={{ '--CircularProgress-size': '10px' }} /> : null}
                                {url.hidden ? 'Unhide' : 'Hide'}
                            </button>
                            <button
                                onClick={() => onAskConfirmDelete(url.id)}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-400 opacity-0 transition-all hover:bg-rose-50 hover:text-rose-600 group-hover:opacity-100"
                                aria-label="Remove domain"
                            >
                                <DeleteOutline sx={{ fontSize: 16 }} />
                            </button>
                        </div>
                    )}
                </td>
            </motion.tr>
        )
    }

    return (
        <div className="space-y-4">
            {/* Add domain card */}
            <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
                <div className="border-b border-gray-100 px-5 py-3">
                    <div className="flex items-center gap-2">
                        <Add sx={{ fontSize: 16 }} className="text-gray-500" />
                        <p className="text-sm font-semibold text-gray-900">Add a domain</p>
                    </div>
                    <p className="mt-0.5 text-[11px] text-gray-500">
                        Bare host like <code className="font-mono text-gray-700">dat.com</code>, or a wildcard like{' '}
                        <code className="font-mono text-gray-700">*.dat.com</code>.
                    </p>
                </div>
                <div className="p-4">
                    <div className="flex gap-2">
                        <div className="flex-1">
                            <input
                                value={newDomain}
                                onChange={(e) => onNewDomainChange(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') onAddUrl()
                                }}
                                disabled={adding}
                                placeholder="dat.com"
                                className={[
                                    'h-9 w-full rounded-lg border bg-white px-3 text-sm font-mono text-gray-900 focus:outline-none focus:ring-2',
                                    addError
                                        ? 'border-rose-300 focus:border-rose-300 focus:ring-rose-500/20'
                                        : 'border-gray-200 focus:border-blue-300 focus:ring-blue-500/20',
                                ].join(' ')}
                            />
                            {addError && <p className="mt-1 text-[11px] text-rose-600">{addError}</p>}
                            {addSuccess && <p className="mt-1 text-[11px] text-emerald-600">✓ {addSuccess}</p>}
                        </div>
                        <button
                            onClick={onAddUrl}
                            disabled={adding || !newDomain.trim()}
                            className="flex h-9 items-center gap-1.5 rounded-lg bg-indigo-600 px-4 text-sm font-medium text-white transition-colors hover:bg-indigo-700 disabled:opacity-60"
                        >
                            {adding ? <CircularProgress size="sm" sx={{ '--CircularProgress-size': '12px', color: '#fff' }} /> : <Add sx={{ fontSize: 16 }} />}
                            Add
                        </button>
                    </div>
                    <label className="mt-2 flex cursor-pointer items-center gap-2 text-[12px] text-gray-600">
                        <input
                            type="checkbox"
                            checked={newHidden}
                            disabled={adding}
                            onChange={(e) => onNewHiddenChange(e.target.checked)}
                            className="h-3.5 w-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500/30"
                        />
                        <span>
                            Hidden — routed through the proxy but <span className="font-medium">not shown</span> to users (for sub-hosts like APIs/CDNs).
                        </span>
                    </label>
                </div>
            </div>

            {/* List card */}
            <div className="rounded-xl border border-gray-100 bg-white shadow-sm">
                <div className="flex items-center gap-2 border-b border-gray-100 px-5 py-3">
                    <Verified sx={{ fontSize: 16 }} className="text-gray-500" />
                    <p className="text-sm font-semibold text-gray-900">Allowed domains</p>
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">{urls.length}</span>
                    <div className="ml-auto w-full max-w-xs">
                        <div className="relative">
                            <SearchIcon sx={{ fontSize: 14 }} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                value={search}
                                onChange={(e) => onSearchChange(e.target.value)}
                                placeholder="Search…"
                                className="h-8 w-full rounded-lg border border-gray-200 bg-white pl-8 pr-3 text-sm focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                            />
                            {search && (
                                <button
                                    onClick={() => onSearchChange('')}
                                    className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                                >
                                    <Close sx={{ fontSize: 13 }} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="flex items-center justify-between gap-2 border-b border-rose-100 bg-rose-50 px-5 py-2.5 text-sm text-rose-700">
                        <span>{error}</span>
                        <button onClick={onClearError} className="rounded p-1 text-rose-600 hover:bg-rose-100">
                            <Close sx={{ fontSize: 14 }} />
                        </button>
                    </div>
                )}

                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-gray-50/80 text-left">
                                <th className="px-5 py-3 text-xs font-semibold text-gray-600">Domain</th>
                                <th className="px-2 py-3 text-xs font-semibold text-gray-600">Added by</th>
                                <th className="px-2 py-3 text-xs font-semibold text-gray-600">Added</th>
                                <th className="px-2 py-3 text-xs font-semibold text-gray-600">Source</th>
                                <th className="px-2 py-3 pr-5 text-right text-xs font-semibold text-gray-600">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={5} className="py-12 text-center">
                                        <CircularProgress size="sm" />
                                    </td>
                                </tr>
                            ) : urls.length === 0 ? (
                                <tr>
                                    <td colSpan={5}>
                                        <EmptyState
                                            icon={<Verified sx={{ fontSize: 26 }} className="text-gray-400" />}
                                            title={search ? 'No domains match your search' : 'No allowed domains yet'}
                                            hint={search ? 'Try a different search term.' : 'Add the first domain above to start proxying traffic.'}
                                        />
                                    </td>
                                </tr>
                            ) : (
                                <>
                                    {visibleUrls.length > 0 && (
                                        <tr className="bg-gray-50/60">
                                            <td colSpan={5} className="px-5 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                                                Visible
                                            </td>
                                        </tr>
                                    )}
                                    {visibleUrls.map((url, idx) => renderUrlRow(url, idx))}
                                    {hiddenUrls.length > 0 && (
                                        <tr className="bg-gray-50/60">
                                            <td colSpan={5} className="px-5 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                                                Hidden
                                            </td>
                                        </tr>
                                    )}
                                    {hiddenUrls.map((url, idx) => renderUrlRow(url, idx))}
                                </>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// BlocklistCard — operator-managed forced-DIRECT domains. Never shown to end users.
// ─────────────────────────────────────────────────────────────────────────────

interface BlocklistCardProps {
    urls: ProxyAllowlistUrlDto[]
    loading: boolean
    error: string
    search: string
    onSearchChange: (s: string) => void
    newDomain: string
    onNewDomainChange: (v: string) => void
    onAddUrl: () => void
    adding: boolean
    addError: string
    addSuccess: string
    confirmDeleteId: string | null
    onAskConfirmDelete: (id: string | null) => void
    onDeleteConfirmed: (id: string) => void
    pendingAction: string | null
    onClearError: () => void
}

function BlocklistCard(props: BlocklistCardProps) {
    const {
        urls,
        loading,
        error,
        search,
        onSearchChange,
        newDomain,
        onNewDomainChange,
        onAddUrl,
        adding,
        addError,
        addSuccess,
        confirmDeleteId,
        onAskConfirmDelete,
        onDeleteConfirmed,
        pendingAction,
        onClearError,
    } = props

    return (
        <div className="space-y-4">
            {/* Add blocked domain card */}
            <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
                <div className="border-b border-gray-100 px-5 py-3">
                    <div className="flex items-center gap-2">
                        <Block sx={{ fontSize: 16 }} className="text-rose-500" />
                        <p className="text-sm font-semibold text-gray-900">Block a domain</p>
                    </div>
                    <p className="mt-0.5 text-[11px] text-gray-500">
                        The extension forces these <span className="font-medium">DIRECT</span> — never routed through the proxy, even if they match an allowed
                        wildcard. Never shown to end users.
                    </p>
                </div>
                <div className="p-4">
                    <div className="flex gap-2">
                        <div className="flex-1">
                            <input
                                value={newDomain}
                                onChange={(e) => onNewDomainChange(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') onAddUrl()
                                }}
                                disabled={adding}
                                placeholder="ads.dat.com"
                                className={[
                                    'h-9 w-full rounded-lg border bg-white px-3 text-sm font-mono text-gray-900 focus:outline-none focus:ring-2',
                                    addError
                                        ? 'border-rose-300 focus:border-rose-300 focus:ring-rose-500/20'
                                        : 'border-gray-200 focus:border-rose-300 focus:ring-rose-500/20',
                                ].join(' ')}
                            />
                            {addError && <p className="mt-1 text-[11px] text-rose-600">{addError}</p>}
                            {addSuccess && <p className="mt-1 text-[11px] text-emerald-600">✓ {addSuccess}</p>}
                        </div>
                        <button
                            onClick={onAddUrl}
                            disabled={adding || !newDomain.trim()}
                            className="flex h-9 items-center gap-1.5 rounded-lg bg-rose-600 px-4 text-sm font-medium text-white transition-colors hover:bg-rose-700 disabled:opacity-60"
                        >
                            {adding ? (
                                <CircularProgress size="sm" sx={{ '--CircularProgress-size': '12px', color: '#fff' }} />
                            ) : (
                                <Block sx={{ fontSize: 16 }} />
                            )}
                            Block
                        </button>
                    </div>
                </div>
            </div>

            {/* List card */}
            <div className="rounded-xl border border-gray-100 bg-white shadow-sm">
                <div className="flex items-center gap-2 border-b border-gray-100 px-5 py-3">
                    <Block sx={{ fontSize: 16 }} className="text-gray-500" />
                    <p className="text-sm font-semibold text-gray-900">Blocked domains</p>
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">{urls.length}</span>
                    <div className="ml-auto w-full max-w-xs">
                        <div className="relative">
                            <SearchIcon sx={{ fontSize: 14 }} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                value={search}
                                onChange={(e) => onSearchChange(e.target.value)}
                                placeholder="Search…"
                                className="h-8 w-full rounded-lg border border-gray-200 bg-white pl-8 pr-3 text-sm focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                            />
                            {search && (
                                <button
                                    onClick={() => onSearchChange('')}
                                    className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                                >
                                    <Close sx={{ fontSize: 13 }} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="flex items-center justify-between gap-2 border-b border-rose-100 bg-rose-50 px-5 py-2.5 text-sm text-rose-700">
                        <span>{error}</span>
                        <button onClick={onClearError} className="rounded p-1 text-rose-600 hover:bg-rose-100">
                            <Close sx={{ fontSize: 14 }} />
                        </button>
                    </div>
                )}

                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-gray-50/80 text-left">
                                <th className="px-5 py-3 text-xs font-semibold text-gray-600">Domain</th>
                                <th className="px-2 py-3 text-xs font-semibold text-gray-600">Added by</th>
                                <th className="px-2 py-3 text-xs font-semibold text-gray-600">Added</th>
                                <th className="px-2 py-3 pr-5 text-right text-xs font-semibold text-gray-600">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={4} className="py-12 text-center">
                                        <CircularProgress size="sm" />
                                    </td>
                                </tr>
                            ) : urls.length === 0 ? (
                                <tr>
                                    <td colSpan={4}>
                                        <EmptyState
                                            icon={<Block sx={{ fontSize: 26 }} className="text-gray-400" />}
                                            title={search ? 'No blocked domains match your search' : 'No blocked domains yet'}
                                            hint={search ? 'Try a different search term.' : 'Block a domain above to force it DIRECT for every user.'}
                                        />
                                    </td>
                                </tr>
                            ) : (
                                urls.map((url, idx) => {
                                    const isConfirming = confirmDeleteId === url.id
                                    const isBusy = pendingAction === url.id
                                    return (
                                        <motion.tr
                                            key={url.id}
                                            initial={{ opacity: 0, x: -8 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ duration: 0.2, delay: idx * 0.02, ease }}
                                            className="group border-b border-gray-100 transition-colors duration-150 last:border-b-0 hover:bg-rose-50/40"
                                        >
                                            <td className="px-5 py-3">
                                                <span className="rounded bg-rose-50 px-2 py-1 font-mono text-sm text-rose-800 ring-1 ring-inset ring-rose-200">
                                                    {url.domain}
                                                </span>
                                            </td>
                                            <td className="px-2 py-3 text-sm text-gray-700">
                                                {url.addedBy === 'system' ? (
                                                    <span className="inline-flex items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600">
                                                        system
                                                    </span>
                                                ) : (
                                                    (url.addedByEmail ?? '—')
                                                )}
                                            </td>
                                            <td className="px-2 py-3">
                                                <p className="text-sm text-gray-600">{fmtRelative(url.createdAt)}</p>
                                                <p className="text-[11px] text-gray-400">{fmtDate(url.createdAt)}</p>
                                            </td>
                                            <td className="px-2 py-3 pr-5 text-right">
                                                {isConfirming ? (
                                                    <div className="inline-flex items-center gap-1.5">
                                                        <span className="text-xs text-gray-500">Remove?</span>
                                                        <button
                                                            onClick={() => onDeleteConfirmed(url.id)}
                                                            disabled={isBusy}
                                                            className="flex h-7 items-center gap-1 rounded-md bg-rose-600 px-2.5 text-xs font-medium text-white transition-colors hover:bg-rose-700 disabled:opacity-60"
                                                        >
                                                            {isBusy ? (
                                                                <CircularProgress size="sm" sx={{ '--CircularProgress-size': '10px', color: '#fff' }} />
                                                            ) : null}
                                                            Yes
                                                        </button>
                                                        <button
                                                            onClick={() => onAskConfirmDelete(null)}
                                                            disabled={isBusy}
                                                            className="flex h-7 items-center rounded-md border border-gray-200 bg-white px-2.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-60"
                                                        >
                                                            No
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => onAskConfirmDelete(url.id)}
                                                        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-400 opacity-0 transition-all hover:bg-rose-50 hover:text-rose-600 group-hover:opacity-100"
                                                        aria-label="Remove blocked domain"
                                                    >
                                                        <DeleteOutline sx={{ fontSize: 16 }} />
                                                    </button>
                                                )}
                                            </td>
                                        </motion.tr>
                                    )
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// EmptyState
// ─────────────────────────────────────────────────────────────────────────────

function EmptyState({ icon, title, hint }: { icon?: React.ReactNode; title: string; hint?: string }) {
    return (
        <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
            {icon && <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">{icon}</div>}
            <p className="text-sm font-semibold text-gray-900">{title}</p>
            {hint && <p className="mt-1 max-w-sm text-sm text-gray-500">{hint}</p>}
        </div>
    )
}
